import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { ShieldQuestion, ShieldAlert } from "lucide-react";
import { mfaApi } from "../../services/mfaApi";
import { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../hooks/useAuth";
import { useLocalization } from "../../hooks/useLocalization";
import { getLandingRouteForUser } from "../../lib/roleRouting";
import { LanguageSwitcher } from "../../components/LanguageSwitcher";

export default function MfaVerifyPage() {
  const { t } = useLocalization();
  const { applyAuthenticatedUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const challengeToken = location.state?.challengeToken;

  const [useBackup, setUseBackup] = useState(false);
  const [code, setCode] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!challengeToken) {
    return (
      <div className="min-h-screen bg-blue-950 flex items-center justify-center p-6" data-testid="mfa-verify-expired">
        <div className="bg-white rounded-2xl p-8 max-w-sm text-center shadow-2xl">
          <ShieldAlert className="mx-auto text-red-500 mb-3" size={40} />
          <p className="text-sm text-gray-600 mb-6">{t("mfa.sessionExpired")}</p>
          <Link to="/staff/login" className="text-sm font-semibold text-blue-600 hover:underline">{t("mfa.backToLogin")}</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await mfaApi.challengeVerify(challengeToken, useBackup ? undefined : code, useBackup ? backupCode : undefined);
      applyAuthenticatedUser(data);
      const target = data.must_change_password ? "/change-password" : getLandingRouteForUser(data);
      navigate(target, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t("mfa.invalidCode"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-blue-950 flex items-center justify-center p-6" data-testid="mfa-verify-page">
      <div className="absolute top-6 right-6"><LanguageSwitcher /></div>
      <div className="w-full max-w-sm bg-white rounded-2xl p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-2">
          <ShieldQuestion className="text-blue-600" size={28} />
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">{t("mfa.challengeTitle")}</h1>
        </div>
        <p className="text-sm text-gray-500 mb-6">{t("mfa.challengeSubtitle")}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!useBackup ? (
            <div>
              <label htmlFor="mfa-challenge-code" className="sr-only">{t("mfa.enterCodeLabel")}</label>
              <input
                id="mfa-challenge-code"
                data-testid="mfa-challenge-code-input"
                type="text"
                required
                autoFocus
                maxLength={6}
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 text-center text-lg font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          ) : (
            <div>
              <label htmlFor="mfa-backup-code" className="text-sm font-medium text-gray-700 mb-1 block">{t("mfa.backupCodeLabel")}</label>
              <input
                id="mfa-backup-code"
                data-testid="mfa-backup-code-input"
                type="text"
                required
                autoFocus
                value={backupCode}
                onChange={(e) => setBackupCode(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 text-center font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
          {error && <p className="text-sm text-red-500" data-testid="mfa-challenge-error">{error}</p>}
          <button
            data-testid="mfa-challenge-submit-button"
            type="submit"
            disabled={submitting}
            className="w-full py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-50"
          >
            {submitting ? t("common.loading") : t("mfa.verify")}
          </button>
        </form>

        <button
          data-testid="mfa-toggle-backup-code-button"
          onClick={() => { setUseBackup((v) => !v); setError(""); }}
          className="mt-4 text-xs font-medium text-blue-600 hover:underline block mx-auto"
        >
          {useBackup ? t("mfa.backToCode") : t("mfa.useBackupCode")}
        </button>
      </div>
    </div>
  );
}
